import { useCallback, useEffect } from "react"
import NotFound from "../../pages/system/NotFound";
import { formatNumber, toValidNumber } from "../../utils/helpers";
import withModal from "../../utils/hoc/withModal";
import PostSkeleton from "../../skeletons/PostSkeleton";
import { useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../redux-stores/store";
import { useDispatch } from "react-redux";
import { fetchInfluencerPost, fetchSocialPostThunk } from "../../redux-stores/slices/postsSlice";

const InfluencerSocialPost: React.FC<{ selectedSocialPost: { socialPostId: string, socialAccountId: string } | null }> = ({ selectedSocialPost }: { selectedSocialPost: { socialPostId: string, socialAccountId: string } | null }) => {

    const dispatch: AppDispatch = useDispatch();

    const postData = useSelector((state: RootState) => selectedSocialPost ? state.posts.social_posts[selectedSocialPost.socialAccountId]?.[selectedSocialPost.socialPostId] : undefined);
    const post = postData?.post || null;
    const platform = postData?.post?.platform;

    const loading = useSelector((state: RootState) => state.posts.loading);

    const fetchSocialPost = useCallback(async () => {
        if (selectedSocialPost) {
            await dispatch(fetchSocialPostThunk({ socialPostId: selectedSocialPost.socialPostId, socialAccountId: selectedSocialPost.socialAccountId }));
        }
    }, [dispatch, selectedSocialPost]);

    useEffect(() => {
        fetchSocialPost()
    }, [selectedSocialPost, fetchSocialPost])

    if (!selectedSocialPost) return <NotFound />

    if (loading) return <PostSkeleton />

    return (
        <div className="row gx-0">
            {/* Media Display */}
            <div className="col-md-12">
                {post?.ig ? (
                    post.ig?.media_url ? (
                        <div className="bg-dark h-100 d-flex align-content-center justify-content-center align-items-stretch">
                            {post.ig.media_type === "VIDEO" ? (
                                <video
                                    id="video_player"
                                    className="mw-100 mh-800px align-self-center"
                                    src={post.ig.media_url}
                                    autoPlay
                                    controls
                                    style={{ objectFit: "contain", height: '52vh' }}
                                />
                            ) : (
                                <img
                                    src={post.ig.media_url}
                                    className="mw-100 mh-800px align-self-center"
                                    style={{ objectFit: "contain", height: '52vh' }}
                                    alt="Instagram media"
                                />
                            )}
                        </div>
                    ) : (
                        <div className="bg-dark h-100 d-flex align-items-center justify-content-center">
                            <a href={post.ig.permalink} target="_blank" className="btn btn-primary" rel="noopener noreferrer">
                                View on Instagram
                            </a>
                        </div>
                    )
                ) : post?.yt ? (
                    <div>
                        <div className="bg-dark h-100 d-flex align-content-center justify-content-center align-items-stretch">
                            <iframe
                                src={post.yt.permalink ? `https://www.youtube.com/embed/${post.yt.permalink.split('/').pop()?.split('?')[0]}` : ""}
                                className="mw-100 mh-800px align-self-center"
                                style={{ objectFit: "contain", height: '52vh', width: '100%' }}
                                frameBorder="0"
                                allowFullScreen
                                title="YouTube video"
                            />
                        </div>
                    </div>
                ) : null}
            </div>

            {/* Post Details */}
            {post && (
                <div className="col-md-12 position-relative">
                    <div className="mh-800px min-h-700px" style={{ overflowY: "auto" }}>
                        {/* Post Caption */}
                        <div className="card card-style mt-3 border-0 rounded-card mb-4">
                            <div className="card-body">
                                <span className="d-block text-muted mb-1 font-10">{post.posted_on}</span>
                                {
                                    platform === 'ig' ? (
                                        <>{post.ig?.caption && <p style={{ whiteSpace: "pre-wrap" }}>{post.ig.caption}</p>}</>
                                    ) : platform === 'yt' ? (
                                        <>{post.yt?.title && <p style={{ whiteSpace: "pre-wrap" }}>{post.yt.title}</p>}</>
                                    ) : null
                                }
                            </div>
                        </div>

                        {/* Analytics Section */}
                        <div className="mx-3">
                            <div className="d-flex flex-row px-1">
                                <h6 className="pop mb-3 ">ANALYTICS</h6>
                                {
                                    platform === 'ig' && post.ig?.permalink && post.type !== "STORY" && (
                                        <a className="ms-auto" href={post.ig.permalink} target="_blank" rel="noopener noreferrer">
                                            <i className="fa-solid fa-arrow-up-right-from-square"></i> Instagram
                                        </a>
                                    )
                                }
                                {platform === 'yt' && post.yt?.permalink && (
                                    <a className="ms-auto" href={post.yt.permalink} target="_blank" rel="noopener noreferrer">
                                        <i className="fa-solid fa-arrow-up-right-from-square"></i> YouTube
                                    </a>
                                )}

                            </div>

                            <div className="card m-0 card-style rounded-card">
                                {/* Influns Intelligence */}
                                {post.summary && (
                                    <div className="card-body d-flex flex-column border-bottom">
                                        <h6>Influns Intelligence</h6>
                                        {/* {post.topic && <p className="fw-bold mb-0">{post.topic}</p>} */}
                                        {post.summary && <p style={{ textAlign: "justify" }}>{post.summary}</p>}
                                        {/* {post.tone && (
                                            <div className="d-flex flex-row mt-2">
                                                <span>Tone</span> <span className="ms-auto">{post.tone}</span>
                                            </div>
                                        )} */}
                                        {post.brands && (
                                            <div className="d-flex flex-row mt-2">
                                                <span>Brand Mentions</span> <span className="ms-auto">{post.brands}</span>
                                            </div>
                                        )}
                                        {post.languages && (
                                            <div className="d-flex flex-row mt-2">
                                                <span>Language</span> <span className="ms-auto">{post.languages}</span>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Interactions */}
                                {post && (
                                    <>
                                        <div className="card-body d-flex flex-column border-bottom">
                                            <h6 className="font-14">Interactions</h6>
                                            {/* <div className="d-flex flex-row mt-2">
                                                <span className="fw-bold">{post.media_product_type_name} Interactions</span>
                                                <span className="ms-auto fw-bold">{post.insights.engagement.total_interactions.value}</span>
                                            </div> */}
                                            <div className="d-flex flex-row mt-2">
                                                <span>
                                                    <i className="fa-regular fa-heart d-inline-block w-15px"></i> Likes
                                                </span>
                                                <span className="ms-auto">{formatNumber(toValidNumber((post?.[platform as keyof typeof post] as any)?.like_count))}</span>
                                            </div>
                                            <div className="d-flex flex-row mt-2">
                                                <span>
                                                    <i className="fa-regular fa-comment d-inline-block w-15px"></i> Comments
                                                </span>
                                                <span className="ms-auto">{formatNumber(toValidNumber((post?.[platform as keyof typeof post] as any)?.comments_count))}</span>
                                            </div>
                                            <div className="d-flex flex-row mt-2">
                                                <span>
                                                    <i className="fa-regular fa-eye d-inline-block w-15px"></i> Views
                                                </span>
                                                <span className="ms-auto">{formatNumber(toValidNumber((post?.[platform as keyof typeof post] as any)?.views_count))}</span>
                                            </div>
                                            {
                                                platform === 'ig' &&
                                                <>
                                                    <div className="d-flex flex-row mt-2">
                                                        <span>
                                                            <i className="fa-regular fa-share-from-square d-inline-block w-15px"></i> Shares
                                                        </span>
                                                        <span className="ms-auto">{formatNumber(toValidNumber((post?.[platform as keyof typeof post] as any)?.share_count))}</span>
                                                    </div>
                                                    <div className="d-flex flex-row mt-2">
                                                        <span>
                                                            <i className="fa-solid fa-chart-line d-inline-block w-15px"></i> Reach
                                                        </span>
                                                        <span className="ms-auto">{formatNumber(toValidNumber((post?.[platform as keyof typeof post] as any)?.reach_count))}</span>
                                                    </div>
                                                </>
                                            }
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

export default withModal(InfluencerSocialPost)